<template>
  <div class="sub-header print-hidden">
    <ContactForm
      :open="contactFormOpen"
      :user-information="userInformation"
      :contact-information="contactInformation"
      :recaptcha-sitekey="recaptchaSitekey"
      :contactOptions="contactOptions"
      :id="id"
      :vendor-information="vendorInformation"
      :origin-source="type"
      ref="contactForm"
    />

    <div class="header__container container">
      <div class="sub-header__back-button-container">
        <button
          v-if="fromOwnWebsite"
          type="button"
          class="button sub-header__button-jump-link sub-header__back-button"
          @click.prevent="backLinkClicked()"
        >
          <Arrow direction="left"></Arrow>
          Tilbage
        </button>
      </div>
      <div class="sub-header--button-panel sub-header--low-res">
        <button
          type="button"
          class="button button--highlight first"
          @click.prevent="contactClickedMobile()"
        >
          Kontakt
        </button>
        <button
          type="button"
          class="button button--highlight last"
          v-if="contactInformation?.phone"
          @click.prevent="callClickedMobile()"
        >
          Ring op
        </button>
      </div>
      <div class="sub-header--button-panel sub-header--high-res">
        <button
          type="button"
          class="button sub-header__button-jump-link"
          :class="{
            'sub-header__button--active': contentInView == 'ad-description',
          }"
          @click.prevent="jumpLinkClicked('ad-description')"
        >
          Beskrivelse
        </button>
        <button
          type="button"
          class="button sub-header__button-jump-link"
          :class="{
            'sub-header__button--active': contentInView == 'ad-specifications',
          }"
          @click.prevent="jumpLinkClicked('ad-specifications')"
        >
          Specifikationer
        </button>
        <button
          type="button"
          class="button sub-header__button-jump-link"
          :class="{
            'sub-header__button--active': contentInView == 'ad-insurance',
            'sub-header__button-jump-link--collapsed': !insuranceLoaded,
          }"
          @click.prevent="insuranceJumplinkClicked()"
        >
          Forsikring
        </button>
        <button
          type="button"
          class="button sub-header__button-jump-link"
          :class="{
            'sub-header__button--active': contentInView == 'ad-financing',
            'sub-header__button-jump-link--collapsed': !financingLoaded,
          }"
          @click.prevent="financeJumplinkClicked()"
        >
          Finansiering
        </button>
        <button
          type="button"
          class="button button--highlight sub-header__button-contact-link"
          v-if="contactOptions.length > 0 && !onlineSalesButton"
          @click.prevent="contactClicked()"
        >
          Kontakt {{ vendorInformation.singular }}
        </button>
        <button
          type="button"
          class="button button--highlight sub-header__button-online-sales"
          v-if="onlineSalesButton"
        >
          <OnlineSalesButton :origin="'SubHeaderAd'"></OnlineSalesButton>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { app } from '@/main'
import ContactForm from '@components/ContactForm/ContactForm.vue'
import OnlineSalesButton from '@components/OnlineSales/OnlineSalesButton.vue'

import Arrow from '@shared/Arrow/Arrow.vue'

import {
  LogTopMobilePhoneCallClicked,
  AdTopFinanceClicked,
  AdTopInsuranceClicked,
  LogTopContactClicked,
  LogTopMobileContactClicked,
  LogFacebookAddToWishlist,
} from '@/events'

export default {
  components: {
    ContactForm,
    OnlineSalesButton,
    Arrow,
  },
  props: {
    userInformation: { default: null, type: Object },
    contactInformation: { default: null, type: Object },
    recaptchaSitekey: { default: '', type: String },
    contactOptions: { default: () => [], type: Array },
    id: { default: undefined, type: Number },
    vendorInformation: { default: undefined, type: Object },
    onlineSalesButton: { default: false, type: Boolean },
  },
  data() {
    return {
      contentInView: '',
      contactFormOpen: false,
      financingLoaded: false,
      insuranceLoaded: false,
      fromOwnWebsite: false,
      type: 'Ad',
    }
  },
  methods: {
    callClickedMobile() {
      if (!this.contactInformation) return
      LogTopMobilePhoneCallClicked(this.type, this.id)

      window.open(this.contactInformation.phoneUrl, '_self')
    },
    contactClicked() {
      this.contactClickedHandler()
      LogTopContactClicked(this.type)
      LogFacebookAddToWishlist()
    },
    contactClickedMobile() {
      this.contactClickedHandler()
      LogTopMobileContactClicked(this.type)
      LogFacebookAddToWishlist()
    },
    contactClickedHandler() {
      this.$refs.contactForm.openForm()
    },
    financeModuleLoaded() {
      this.financingLoaded = true
    },
    insuranceModuleLoaded() {
      this.insuranceLoaded = true
    },
    backLinkClicked() {
      if (!this.fromOwnWebsite) return

      if (history.length > 1) {
        // Forsøger at gå tilbage i history. Er man allerede på første index i history, vil denne ikke gøre noget og man rammer derfor timeout'en nedenfor
        history.back()
      }

      // Denne kommer ex. i spil hvis ctrl+click er brugt, da der ikke er ekstra history
      // Også hvis brugeren har opbygget history og er kommet tilbage til annoncen åbnet med ctrl+click, da denne ikke kan gå tilbage i history
      setTimeout(() => {
        location.href = document.referrer
      }, 200)
    },
    financeJumplinkClicked() {
      this.jumpLinkClicked('ad-financing')
      AdTopFinanceClicked()
      LogFacebookAddToWishlist()
    },
    insuranceJumplinkClicked() {
      this.jumpLinkClicked('ad-insurance')
      AdTopInsuranceClicked()
      LogFacebookAddToWishlist()
    },
    financeJumplinkHandler() {
      this.jumpLinkClicked('ad-financing')
    },
    insuranceJumplinkHandler() {
      this.jumpLinkClicked('ad-insurance')
    },
    jumpLinkClicked(hashName) {
      const element = document.getElementById(hashName)
      if (element == null) return
      this.contentInView = hashName

      const elementPosition = element.offsetTop
      // Depend on the scroll direction. When scrolling upwards, then main menu will be shown, thus the greater offset
      const headerOffset = elementPosition > window.scrollY ? 70 : 120
      const offsetPosition = elementPosition - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    },
    handleScroll() {
      const scrollY = window.scrollY + 120

      if (this.isContentElementInView('ad-description', scrollY))
        this.contentInView = 'ad-description'
      else if (this.isContentElementInView('ad-specifications', scrollY))
        this.contentInView = 'ad-specifications'
      else if (this.isContentElementInView('ad-insurance', scrollY))
        this.contentInView = 'ad-insurance'
      else if (this.isContentElementInView('ad-financing', scrollY))
        this.contentInView = 'ad-financing'
      else this.contentInView = ''
    },
    isContentElementInView(elementId, scrollY) {
      const element = document.getElementById(elementId)
      if (element === null) return 0
      const elementTop = element.offsetTop
      const elementEnd = element.offsetTop + element.offsetHeight

      if (elementTop < scrollY + 80 && elementEnd - 80 > scrollY) return true

      return false
    },
  },
  created() {
    this.fromOwnWebsite = document.referrer.startsWith(location.origin)

    window.addEventListener('scroll', this.handleScroll)
    app.mitt.on('click-on-contact', this.contactClickedHandler)
    app.mitt.on('click-on-company-contact', this.contactClickedHandler)
    app.mitt.on('finance-module-loaded', this.financeModuleLoaded)
    app.mitt.on('insurance-module-loaded', this.insuranceModuleLoaded)
    app.mitt.on('finance-jumplink-triggered', this.financeJumplinkHandler)
    app.mitt.on('insurance-jumplink-triggered', this.insuranceJumplinkHandler)
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll)
  },
}
</script>

<style lang="scss">
@import '../SubHeader/SubHeader.scss';
</style>
